import React, { useState, useEffect } from 'react';
import { Modal, Nav, Form, Button, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import authApi from '../../api/auth/authApi';
import locationApi from '../../api/locations/locationApi';
import { STORAGE_KEYS } from '../../api/config';

const WatoaHudumaModal = ({ show, onHide, categories, defaultTab }) => {
  const navigate = useNavigate();
  const [modalTab, setModalTab] = useState('login');
  const [signupStep, setSignupStep] = useState(1);
  const [otpSent, setOtpSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [authorizationCode, setAuthorizationCode] = useState('');
  const [formMessage, setFormMessage] = useState(null);

  const [formData, setFormData] = useState({
    fullName: '',
    phoneEmail: '',
    otp: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    serviceCategory: [],
    location: '',
    acceptTerms: false
  });

  useEffect(() => {
    if (show) {
      setModalTab(defaultTab || 'login');
      if (defaultTab === 'register') {
        setSignupStep(1);
        setOtpSent(false);
      }
    }
  }, [show, defaultTab]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const loadLocationOptions = async (inputValue) => {
    if (inputValue.length < 3) {
      return [];
    }
    const results = await locationApi.searchLocations(inputValue);
    return results.map(loc => ({
      value: loc.id,
      label: loc.name || loc.title || loc
    }));
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const username = e.target.username.value;
    const password = e.target.password.value;

    if (!username || !password) {
      toast.error('Tafadhali jaza username na password');
      return;
    }

    setSubmitting(true);
    try {
      let loginUsername = username.trim();
      const response = await authApi.login({ username: loginUsername, password });

      if (response.success || response.status === 800) {
        localStorage.removeItem('show_change_password_modal');
        localStorage.removeItem('show_select_event_modal');
        localStorage.removeItem('temp_auth_code');
        localStorage.removeItem('reset_mobile');

        if (response.data?.token) {
          localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.token);
        }

        if (response.data?.user_data) {
          const userDataToStore = { ...response.data.user_data };
          if (response.data.events) {
            userDataToStore.events = response.data.events;
          }
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userDataToStore));
          localStorage.setItem(STORAGE_KEYS.USER_ID, userDataToStore.id);
        }

        if (response.data?.events && response.data.events[0]) {
          localStorage.setItem(STORAGE_KEYS.UID, response.data.events[0].event_address);
          localStorage.setItem(STORAGE_KEYS.EVENT_ID, response.data.events[0].id);
          localStorage.setItem('selectedEventId', response.data.events[0].id?.toString());
        }

        toast.success('Karibu tena!');
        onHide();

        const userRole = response.data?.user_data?.role;
        if (userRole === 1) {
          window.location.href = '/dashboard';
        } else if (userRole === 3 || userRole === 'provider' || response.data?.user_data?.event_type === 'Watoa Huduma') {
          window.location.href = '/service-provider';
        } else {
          window.location.href = '/dashboard';
        }
      } else {
        toast.error(response.message || 'Namba/barua pepe au namba ya siri si sahihi');
      }
    } catch (err) {
      toast.error(err.message || 'Hitilafu kwenye kuingia. Jaribu tena.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.phoneEmail.trim()) {
      toast.error('Tafadhali jaza jina na namba ya simu');
      return;
    }

    setSubmitting(true);
    try {
      const response = await authApi.requestOtp({ number: formData.phoneEmail });
      if (response.success || response.status === 800 || response.data?.otp_sent) {
        setOtpSent(true);
        toast.success('OTP imetumwa kwenye namba yako');
      } else {
        toast.error(response.message || 'Hitilafu kutuma OTP');
      }
    } catch (err) {
      toast.error('Hitilafu kutuma OTP');
      setTimeout(() => setOtpSent(true), 2000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!formData.otp.trim()) {
      toast.error('Tafadhali ingiza OTP');
      return;
    }

    setSubmitting(true);
    try {
      const response = await authApi.verifyOtp({ number: formData.phoneEmail, otp: formData.otp });
      if (response.success || response.status === 800) {
        const code = response.data?.authorization_code || response.data?.token || response.data;
        if (code) {
          setAuthorizationCode(code.authorization_code || code.token || code);
        }

        // Check if user already exists (indicated by user_data or events in response)
        const userData = response.data?.user_data || response.data?.user;
        if (userData) {
          if (response.data.token) {
            localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.token);
          }
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
          localStorage.setItem(STORAGE_KEYS.USER_ID, userData.id);

          toast.success(response.message || 'Uthibitisho umekamilika! Karibu tena.');
          setTimeout(() => {
            onHide();
            window.location.href = '/service-provider';
          }, 1500);
          return;
        }

        setSignupStep(2);
        toast.success('OTP Imethibitishwa');
      } else {
        toast.error(response.message || 'OTP sio sahihi');
      }
    } catch (err) {
      toast.error('Hitilafu kuthibitisha OTP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreatePassword = (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error("Password hazifanani");
      return;
    }
    setSignupStep(3);
  };

  const handleProviderRegister = async (e) => {
    e.preventDefault();
    if (!formData.acceptTerms) {
      toast.error('Tafadhali kubali vigezo na masharti');
      return;
    }

    setSubmitting(true);
    try {
      const isEmail = formData.phoneEmail.includes('@');
      const mobileNum = parseInt(formData.phoneEmail.replace(/\D/g, ''), 10);

      const formattedMobile = formData.phoneEmail.replace(/\D/g, '');

      const payload = {
        full_name: formData.fullName,
        user_name: formData.phoneEmail,
        password: formData.password,
        role: 3,
        mobile: mobileNum, // Root-level mobile must be an integer to satisfy backend validation rules
        authorization_code: authorizationCode || formData.otp,
        provider: {
          name: formData.businessName || formData.fullName,
          categories: formData.serviceCategory,
          category_id: formData.serviceCategory.length > 0 ? formData.serviceCategory[0] : null,
          location_id: parseInt(formData.location, 10),
          mobile: formattedMobile
        }
      };

      if (isEmail) {
        payload.email = formData.phoneEmail;
      }

      const response = await authApi.registerServiceProvider(payload);
      if (response.success || response.status === 800) {
        setFormMessage('Usajili umekamilika kikamilifu! Inakupeleka kwenye dashibodi...');
        toast.success(response.message || 'Usajili wa Mtoa Huduma umekamilika!');
        if (response.data?.token) localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.token);
        if (response.data?.user_data) {
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data.user_data));
          localStorage.setItem(STORAGE_KEYS.USER_ID, response.data.user_data.id);
        }

        // Use window.location.href to guarantee the auth context gets the new tokens on reload
        setTimeout(() => {
          onHide();
          window.location.href = '/service-provider';
        }, 1500);
      } else {
        toast.error(response.message || 'Hitilafu wakati wa usajili');
      }
    } catch (err) {
      toast.error('Kuna tatizo limejitokeza. Tafadhali jaribu tena.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size={modalTab === 'register' && signupStep === 3 ? 'lg' : 'md'}>
      <Modal.Header closeButton>
        <Modal.Title>Service Provider Portal</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Nav variant="pills" className="nav-fill mb-4">
          <Nav.Item>
            <Nav.Link
              active={modalTab === 'login'}
              onClick={() => setModalTab('login')}
            >
              Ingia
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={modalTab === 'register'}
              onClick={() => { setModalTab('register'); setSignupStep(1); setOtpSent(false); }}
            >
              Jisajili
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {modalTab === 'login' ? (
          <Form onSubmit={handleLoginSubmit}>
            <Form.Group className="mb-3" controlId="username">
              <Form.Label>Namba ya Simu / Email</Form.Label>
              <Form.Control type="text" name="username" placeholder="Ingiza namba au barua pepe" required disabled={submitting} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="password">
              <Form.Label>Password</Form.Label>
              <Form.Control type="password" name="password" placeholder="Password" required disabled={submitting} />
            </Form.Group>
            <Button variant="primary" type="submit" className="w-100 mt-3" style={{ borderRadius: '30px' }} disabled={submitting}>
              {submitting ? 'Inaingia...' : 'Ingia'}
            </Button>
          </Form>
        ) : (
          <>
            {signupStep === 1 && (
              <Form onSubmit={otpSent ? handleVerifyOTP : handleRequestOTP}>
                <div className="text-center mb-3">
                  <p className="text-muted">Hatua ya 1: Uthibitisho</p>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Jina Kamili au Jina la Biashara</Form.Label>
                  <Form.Control type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder="Mfano: Juma Catering au Juma Selemani" required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Namba ya Simu / Email</Form.Label>
                  <Form.Control type="text" name="phoneEmail" value={formData.phoneEmail} onChange={handleInputChange} placeholder="Namba ya Simu au Email" required disabled={otpSent} />
                </Form.Group>

                {otpSent ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Nambari ya Uthibitisho (OTP)</Form.Label>
                      <Form.Control type="text" name="otp" value={formData.otp} onChange={handleInputChange} placeholder="Ingiza nambari uliyotumiwa" required />
                    </Form.Group>
                    <Button variant="primary" type="submit" className="w-100 mt-3" style={{ borderRadius: '30px' }} disabled={submitting}>
                      {submitting ? 'Inathibitisha...' : 'Thibitisha OTP'}
                    </Button>
                  </>
                ) : (
                  <Button variant="primary" type="submit" className="w-100 mt-3" style={{ borderRadius: '30px' }} disabled={submitting}>
                    {submitting ? 'Inatuma...' : 'Omba OTP'}
                  </Button>
                )}
              </Form>
            )}

            {signupStep === 2 && (
              <Form onSubmit={handleCreatePassword}>
                <div className="text-center mb-3">
                  <p className="text-muted">Hatua ya 2: Tengeneza Password</p>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder="Weka password mpya" required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Thibitisha Password</Form.Label>
                  <Form.Control type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} placeholder="Rudia password" required />
                </Form.Group>
                <Button variant="primary" type="submit" className="w-100 mt-3" style={{ borderRadius: '30px' }}>
                  Endelea
                </Button>
              </Form>
            )}

            {signupStep === 3 && (
              <Form onSubmit={handleProviderRegister}>
                <div className="text-center mb-3">
                  <p className="text-muted">Hatua ya 3: Taarifa za Huduma Zako</p>
                </div>
                {formMessage && (
                  <div className="alert alert-success text-center" role="alert">
                    {formMessage}
                  </div>
                )}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Jina la Biashara (Kama ni tofauti na la awali)</Form.Label>
                      <Form.Control type="text" name="businessName" value={formData.businessName} onChange={handleInputChange} placeholder={formData.fullName || "Mfano: Juma Catering"} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Aina ya Huduma</Form.Label>
                      <Select
                        isMulti
                        name="serviceCategory"
                        options={categories.map(cat => ({ value: cat.id, label: cat.category || cat.name }))}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="Tafuta na chagua huduma..."
                        onChange={(selectedOptions) => {
                          setFormData({
                            ...formData,
                            serviceCategory: selectedOptions ? selectedOptions.map(opt => opt.value) : []
                          });
                        }}
                        value={categories
                          .filter(cat => formData.serviceCategory.includes(cat.id))
                          .map(cat => ({ value: cat.id, label: cat.category || cat.name }))}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? '#4169e1' : '#ced4da',
                            borderRadius: '0.375rem',
                            padding: '2px'
                          }),
                          multiValue: (styles) => ({
                            ...styles,
                            backgroundColor: '#e8f0fe',
                            borderRadius: '4px'
                          }),
                          multiValueLabel: (styles) => ({
                            ...styles,
                            color: '#4169e1',
                            fontWeight: '500'
                          }),
                          multiValueRemove: (styles) => ({
                            ...styles,
                            color: '#4169e1',
                            ':hover': {
                              backgroundColor: '#4169e1',
                              color: 'white',
                            },
                          }),
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Mkoa / Eneo</Form.Label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions={false}
                    loadOptions={loadLocationOptions}
                    placeholder="Mfano: Dar es Salaam"
                    noOptionsMessage={({ inputValue }) =>
                      inputValue.length < 3 ? "Andika walau herufi 3 kutafuta" : "Hakuna mahali palipopatikana"
                    }
                    onChange={(selectedOption) => {
                      setFormData({
                        ...formData,
                        location: selectedOption ? selectedOption.value : ''
                      });
                    }}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: state.isFocused ? '#4169e1' : '#ced4da',
                        borderRadius: '0.375rem',
                        padding: '2px'
                      })
                    }}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleInputChange} label="Nakubali vigezo na masharti" required />
                </Form.Group>
                <Button variant="success" type="submit" className="w-100 mt-3" style={{ borderRadius: '30px' }} disabled={submitting}>
                  {submitting ? 'Inasajili...' : 'Kamilisha Usajili'}
                </Button>
              </Form>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default WatoaHudumaModal;
